import type { CSSProperties } from 'vue';
import { defineComponent, h, reactive, ref } from 'vue';

/**
 * It is important to keep states outside the useTooltip composable function.
 * So that it works as a shared state between multiple components.
 */

const isVisible = ref(false);
const text = ref('');

const tooltipStyles = reactive<CSSProperties>({
  position: 'absolute',
  top: '0px',
  left: '0px',
  zIndex: '1000',
  opacity: '.95',
  padding: '6px 12px',
  borderRadius: '4px',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  color: 'var(--shade-7)',
  backgroundColor: 'var(--shade-2)',
  transform: 'translate(-50%, -100%)',
  boxShadow: '0 0 5px rgba(0, 0, 0, 0.3)',
});

/**
 * useTooltip is a composable function that returns a set of methods to show and hide a tooltip.
 * It sets the text content and position of the tooltip based on the arguments passed.
 *
 * @returns {Object} - An object containing methods to show and hide the tooltip.
 */
const useTooltip = () => {
  const showTooltip = (content: string, tooltipPositionX: number, tooltipPositionY: number) => {
    text.value = content;
    tooltipStyles.left = `${tooltipPositionX}px`;
    tooltipStyles.top = `${tooltipPositionY}px`;
    isVisible.value = true;
  };

  const hideTooltip = () => {
    isVisible.value = false;
  };

  return {
    text,
    isVisible,
    tooltipStyles,
    showTooltip,
    hideTooltip,
  };
};

/**
 * BaseTooltip is a component constructed using the render function to attach at root level.
 * Creates a tooltip vnode and returns it if the tooltip is set to be visible.
 * Tooltip vnode is just a div with some styles and inner text as child.
 *
 * @component BaseTooltip
 */
const BaseTooltip = defineComponent({
  setup() {
    return () => {
      const tooltip = h('div', { style: tooltipStyles }, [text.value]);
      return isVisible.value ? tooltip : null;
    };
  },
});

export { BaseTooltip, useTooltip };
